// cleanup_remove_external_libraries_GENERICS
// cleanup_convert_to_typescript_file_GENERICS
// cleanup_convert_to_glu_component_GENERICS

import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce, padEnd, padStart } from 'lodash';
import classnames from 'classnames';

import useLocale from 'components/useLocale';
import { isBrowserSlow } from 'utils/browserSpeed';

import Input from './Input';
import useStyles from './CurrencyInput.styles';

export const exp = (num, pow) => num ** pow;

export const sanitize = (str) => String(str).replace(/[^0-9]/g, '');

export const checkDecimals = (str) => {
  const regex = new RegExp(/\.\d{1,3}/, 'g');

  const match = String(str).match(regex);
  const hasDecimals = !!match;

  return {
    hasDecimals,
    decimals: match && match[0].slice(1)
  };
};

/**
 * Include decimals in a number
 *
 * @param {string|number} num
 */
const includeDecimals = (num) => {
  const decimalsCount = 2;
  const { decimals } = checkDecimals(num);
  const formattedNum = decimals ? parseFloat(num) * exp(10, decimals.length) : num;

  const amount = parseFloat(formattedNum);
  return ((amount * exp(10, decimalsCount)) / exp(100, 2)).toFixed(2);
};

/**
 * Format number with a separator
 * @param {number|string} num
 */
const formatNumber = (num, separator) => {
  if (!num) {
    return num;
  }

  const { decimals, hasDecimals } = checkDecimals(num);
  const n = String(
    includeDecimals(hasDecimals ? Math.round(num * exp(10, decimals.length)) : num)
  );
  const p = n.indexOf('.');
  return n.replace(/\d(?=(?:\d{3})+(?:\.|$))/g, (m, i) =>
    p < 0 || i < p ? `${m}${separator}` : m
  );
};

const isDeleteEvent = (newVal, prevVal) => {
  const newValLength = newVal.replace('.', '0').replace(/^0*/g, '').length;
  const prevValLength = prevVal ? prevVal.replace('.', '0').replace(/^0*/g, '').length : 0;
  return newValLength < prevValLength || (!newValLength && !prevValLength);
};

const normalizeDecimals = (inputVal) => {
  const defValue = String(inputVal);
  const decimalSplit = defValue.split('.');
  if (decimalSplit.length < 2) {
    return defValue;
  }
  const numDecimals = decimalSplit[1].length || 0;
  if (numDecimals === 2) {
    return defValue;
  }
  return `${decimalSplit[0]}.${padEnd(decimalSplit[1].substring(0, 2), 2, '0')}`;
};

export const CurrencyInputComponent = React.forwardRef(
  (
    {
      className: passedClasses,
      debounceInput,
      defaultValue,
      maxLength,
      onBlur: validateBlur,
      onChange: validator,
      onFocus: onFocusCallback,
      validateBlurOnly,
      value,
      // Removing so they do not get passed to input
      /* eslint-disable */
      exact,
      resetErrors,
      resetData,
      disabled,
      /* eslint-enable */
      ...props
    },
    ref
  ) => {
    const { amountField, textRightAlign } = useStyles();
    const separator = ',';
    const defValue = String(defaultValue || value || '');

    const [inputValue, setInputValue] = useState(defValue);

    const debouncedValidator = useMemo(
      () =>
        debounce((event) => {
          validator(event);
        }, 250),
      [validator]
    );

    // Run onchange the first time to get the 0.00.
    // Then debounce
    const validateChange = (...args) => {
      // Add && debounceInput
      if (inputValue && debounceInput && isBrowserSlow()) {
        debouncedValidator(...args);
      } else {
        validator(...args);
      }
    };

    const onChange = (event) => {
      // Sometimes it can happen, when form is improperly handled
      // Multiple updates are coming even with dummy event values
      // We are quiting here, in case when value is "not present"
      // Encountered in: [TaxPayment]
      if (typeof event.target.value !== 'undefined') {
        const currValue = event.target.value || '';
        const { hasDecimals } = checkDecimals(currValue);
        if (!hasDecimals) {
          const updateValue =
            currValue !== ''
              ? includeDecimals(sanitize(padStart(currValue, '3', '0')))
              : currValue;
          validateChange({ target: { value: updateValue } });
          setInputValue(updateValue);
        } else {
          const sanitized = sanitize(currValue);
          const match = sanitized.match(/[0-9]+/g);
          const strLength = match && match.join('').length - 1;
          const value = formatNumber(sanitized, separator);
          const isDelete = isDeleteEvent(value, inputValue);

          if (strLength <= maxLength) {
            const pos = value.startsWith('0.0') ? 5 : event.target.selectionEnd;
            const element = event.target;

            const lengthIncrease =
              value.length > inputValue?.length ? value.length - inputValue.length : 0;

            setTimeout(() => {
              const deletePos =
                value?.length !== inputValue?.length
                  ? pos + 1 - (inputValue.length - value.length)
                  : pos + 1;
              const newPos = isDelete ? deletePos : pos - 1 + lengthIncrease;

              // special case
              const periodBuffer = !isDelete && value.length - 2 === newPos ? 1 : 0;
              if (element.setSelectionRange)
                element.setSelectionRange(newPos - periodBuffer, newPos - periodBuffer);
            }, 0);
            validateChange({ target: { value } });
            setInputValue(value);
          } else {
            validateChange({ target: { value: inputValue } });
          }
        }
      }
    };

    const onFocus = (event) => {
      if (parseFloat(event.target.value) === 0) onChange(event);
    };

    const onFocusEventFired = (event) => {
      onFocus(event);
      if (onFocusCallback) {
        onFocusCallback(event);
      }
      return null;
    };

    const onBlur = (event) => {
      const value = event.target.value;
      const { hasDecimals } = checkDecimals(value);

      // quit when number is already formatted
      if (hasDecimals) {
        return;
      }

      // if there's no value, use 0 instead
      const updatedValue = (!value && includeDecimals(0)) || value;

      setInputValue(updatedValue);
      validateChange({ target: { value: updatedValue } });
    };

    useEffect(() => {
      const { hasDecimals } = checkDecimals(defValue);

      setInputValue(
        !hasDecimals && defValue !== ''
          ? includeDecimals(defValue * 100)
          : normalizeDecimals(defValue)
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue, value]);

    return (
      <input
        inputMode="numeric"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        ref={ref}
        className={classnames(amountField, passedClasses, { [textRightAlign]: !props.readOnly })}
        disabled={disabled}
        onBlur={onBlur}
        onChange={(ev) => onChange(ev)}
        onClick={onFocus}
        onFocus={onFocusEventFired}
        step="0.01"
        value={inputValue ? formatNumber(sanitize(inputValue), separator) : ''}
      />
    );
  }
);

CurrencyInputComponent.displayName = 'CurrencyInputComponent';

CurrencyInputComponent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}).isRequired,
  maxLength: PropTypes.number,
  debounceInput: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  exact: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  validateBlurOnly: PropTypes.bool
};

CurrencyInputComponent.defaultProps = {
  className: '',
  debounceInput: false,
  maxLength: 10,
  onBlur() {},
  onFocus() {},
  onChange() {},
  exact: false,
  value: '',
  defaultValue: '',
  readOnly: false,
  validateBlurOnly: false
};

export const CurrencyValidationComponent = ({
  addon = '$',
  disabled = false,
  readOnly = false,
  ...props
}) => {
  const locale = useLocale();
  const classes = useStyles();

  return (
    <div aria-hidden="true" data-qa="payment-currency-unit">
      <Input
        aria-label={locale['common.amount']}
        classes={classes}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        disabled={disabled}
        inputComponent={CurrencyInputComponent}
        prepend={
          !readOnly ? (
            <span className="input-group-addon" data-qa="input-currency-unit">
              {addon}
            </span>
          ) : null
        }
        readOnly={readOnly}
        rendersInput
      />
    </div>
  );
};

CurrencyValidationComponent.propTypes = {
  addon: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool
};

export default CurrencyValidationComponent;
